import AlertPopup from "components/alert-popup/AlertPopup";
import InfoContext from "pages/InfoContext";
import InfoHeaderAction from "pages/info-header/InfoHeaderAction";
import PersonalSubcontractingCompany from "components/sections-subcontractor-company/PersonalSubcontractingCompany";
import SubcontractorsSubcontractingCompany from "components/sections-subcontractor-company/SubcontractorsSubcontractingCompany";
import _ from "lodash";
import { ENTITY } from "constants/info-header";
import {
  FILTERS_FOR_NAMES,
  HISTORY_SUBCONTRACTOR_BY_FILTERS_KEYS_ID,
  POPUP_STATUS,
  QUERY_STATUS,
  SECTIONS,
} from "constants/constants";
import { useContext, useEffect, useMemo, useState } from "react";
import { useGeneralContext } from "api/directory/general/GeneralProvider";
import { useParams, useNavigate } from "react-router-dom";
import { useSettingsContext } from "api/settings/SettingsProvider";
import { useSubcontractorCompanyContext } from "api/directory/subcontractor-company/SubcontractorCompanyProvider";
import { useSubcontractorCompanyDatatrackContext } from "api/datatrack/subcontractor-company/SubcontractorCompanyDatatrackProvider";
import { useUserContext } from "api/user/UserProvider";
import { useSubcontractorContext } from "api/directory/subcontractor/SubcontractorProvider";
import PageContext from "pages/PageContext";
import ValidationModal from "components/modal/ValidationModal";
import DocumentDownloadModal from "components/modal/DocumentDownloadModal";
import {
  commonOnCloseHandler,
  validationModalTitleHelper,
} from "utils/modalsHelper";
import { statusHandler } from "utils/helper";
import LoaderV2 from "core/loader/LoaderV2";

const DisplaySubcontractingCompany = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const {
    updateSubcontractorCompany,
    editSubcontractorCompanyStatus,
    deleteSubcontractorCompany,
    displaySubcontractorCompanyStatus,
    getSubcontractorCompanyBySlug,
    subcontractorCompany,
    refresh,
    setRefresh,
    setEditSubcontractorCompanyStatus,
    deleteSubcontractorCompanyStatus,
    setDeleteSubcontractorCompanyStatus,
    subcontractorsExportStatus,
    setSubcontractorsExportStatus,
  } = useSubcontractorCompanyContext();
  const {
    getNamesByFilters,
    namesByFilters,
    sirenErrorMessage,
    setSirenErrorMessage,
  } = useGeneralContext();

  const {
    getSubcontractorsBySubcontractorCompany,
    subcontractorsBySubcontractorCompany,
  } = useSubcontractorContext();
  const { lang, userScope } = useUserContext();
  const { getHistorySubcontractorByFilters, historySubCompany } =
    useSubcontractorCompanyDatatrackContext();

  const [isEnabled, setIsEnabled] = useState(false);
  const [readOnly, setReadOnly] = useState(true);
  const [section, setSection] = useState(SECTIONS.PERSONAL);
  const [code, setCode] = useState();
  const [status, setStatus] = useState();
  const { dataCountry, getDataCountry } = useSettingsContext();
  const {
    searchDavidsonBySiren,
    sirenData,
    setSirenData,
    searchUsers,
    usersByIds,
  } = useGeneralContext();
  const { slugSearched, setSlugSearched } = useContext(PageContext);
  const companySlug = slug || slugSearched;
  const isCurrentEntity = subcontractorCompany?.slug === companySlug;
  const [isOpen, setIsOpen] = useState(false);

  const currentQueryStatus = statusHandler({
    statusList: [
      editSubcontractorCompanyStatus,
      deleteSubcontractorCompanyStatus,
    ],
  });

  const validationModalTitle = validationModalTitleHelper({
    isEditAction: editSubcontractorCompanyStatus !== QUERY_STATUS.INITIAL,
    isDeleteAction: deleteSubcontractorCompanyStatus !== QUERY_STATUS.INITIAL,
  });

  useEffect(() => {
    setReadOnly(true);
    getSubcontractorCompanyBySlug(slug || slugSearched);
    setRefresh(false);
  }, [slug, slugSearched, refresh, userScope]);

  useEffect(() => {
    if (displaySubcontractorCompanyStatus === QUERY_STATUS.NOT_FOUND) {
      navigate(`/${userScope}/not-found`, { replace: true });
    }
  }, [displaySubcontractorCompanyStatus, userScope, navigate]);

  useEffect(() => {
    switch (section) {
      case SECTIONS.SUBCONTRACTORS:
        getHistorySubcontractorByFilters(
          subcontractorCompany?.id,
          HISTORY_SUBCONTRACTOR_BY_FILTERS_KEYS_ID.subcontractorCompanyId
        );
        getSubcontractorsBySubcontractorCompany(subcontractorCompany?.id);
        break;
      case SECTIONS.PERSONAL:
        getSubcontractorCompanyBySlug(slug || slugSearched);
        break;
      default:
        break;
    }
  }, [section]);

  const onSubmit = async (info) => {
    window.scrollTo(0, 0);
    // Awaited so the caller's submit lock covers the whole request
    try {
      await updateSubcontractorCompany(subcontractorCompany?.id, {
        ...info,
        nationality_id: info?.nationality[0]?.value || info?.nationality_id,
        partner_connection: Boolean(isEnabled),
        partner_email: isEnabled ? info.partner_email : null,
        country: info?.country[0]?.value || info?.country,
      });
    } catch {
      // Provider already set the entity status to ERROR and apiErrorResponse
    }
  };

  useEffect(() => {
    getDataCountry();
    setSirenData({});
  }, []);

  useEffect(() => {
    if (!_.isEmpty(subcontractorCompany))
      setIsEnabled(subcontractorCompany?.partner_connection);
  }, [subcontractorCompany]);

  useEffect(() => {
    if (!_.isEmpty(historySubCompany)) {
      const ids = historySubCompany
        ?.map((sub) => sub.subcontractor_id)
        ?.filter(Boolean);
      if (ids?.length > 0) {
        getNamesByFilters({
          types: [FILTERS_FOR_NAMES.PERSON],
          id: ids,
          manager_of_id: ids,
        });
      }
    }
  }, [historySubCompany]);

  useEffect(() => {
    currentQueryStatus !== QUERY_STATUS.INITIAL && setIsOpen(true);
    if (editSubcontractorCompanyStatus === QUERY_STATUS.SUCCESS) {
      getSubcontractorCompanyBySlug(subcontractorCompany?.slug);
    }
  }, [currentQueryStatus]);

  useEffect(() => {
    if (sirenErrorMessage) {
      setStatus(POPUP_STATUS.ERROR);
      setCode(sirenErrorMessage);
    }
  }, [sirenErrorMessage]);

  useEffect(() => {
    if (subcontractorsBySubcontractorCompany?.length > 0) {
      const managersIds = subcontractorsBySubcontractorCompany?.map(
        (e) => e?.subcontractor?.manager_id
      );
      Array.isArray(managersIds) &&
        managersIds?.length > 0 &&
        searchUsers({
          ids: managersIds,
        });
    }
  }, [subcontractorsBySubcontractorCompany]);

  const value = useMemo(
    () => ({
      refresh,
      setRefresh,
      dataCountry,
      deleted: subcontractorCompany?.deleted,
      editSubcontractorCompanyStatus,
      entity: ENTITY.SUBCONTRACTING_COMPANY,
      id: subcontractorCompany?.id,
      isEnabled,
      lang,
      onDelete: deleteSubcontractorCompany,
      readOnly,
      searchDavidsonBySiren,
      section,
      setIsEnabled,
      setReadOnly,
      setSection,
      setSirenData,
      sirenData,
      subcontractorCompany,
      searchUsers,
      sirenErrorMessage,
      setSirenErrorMessage,
      setStatus,
      setCode,
      code,
      slug,
    }),
    [
      refresh,
      setRefresh,
      dataCountry,
      subcontractorCompany,
      editSubcontractorCompanyStatus,
      isEnabled,
      lang,
      deleteSubcontractorCompany,
      readOnly,
      searchDavidsonBySiren,
      section,
      setIsEnabled,
      setReadOnly,
      setSection,
      setSirenData,
      sirenData,
      searchUsers,
      sirenErrorMessage,
      setSirenErrorMessage,
      setStatus,
      setCode,
      code,
      slug,
    ]
  );

  return (
    <InfoContext.Provider value={value}>
      <AlertPopup
        code={code}
        setCode={setCode}
        setStatus={setStatus}
        status={status}
      />
      <DocumentDownloadModal
        status={subcontractorsExportStatus}
        onClose={() => setSubcontractorsExportStatus(QUERY_STATUS.INITIAL)}
      />
      <ValidationModal
        status={currentQueryStatus}
        open={isOpen}
        onClose={() =>
          commonOnCloseHandler({
            editEntityStatus: editSubcontractorCompanyStatus,
            deleteEntityStatus: deleteSubcontractorCompanyStatus,
            setDeleteEntityStatus: setDeleteSubcontractorCompanyStatus,
            setEditEntityStatus: setEditSubcontractorCompanyStatus,
            setIsOpen,
            setReadOnly,
            userScope,
            navigate,
          })
        }
        messages={{
          pendingTitleId: validationModalTitle,
          pendingMessageId: "modal_validation_file_pending_msg",
          successTitleId: validationModalTitle,
          successMessageId: "modal_validation_file_success_msg",
          errorTitleId: validationModalTitle,
          errorMessageId: "modal_validation_file_error_msg",
        }}
      />
      {(!isCurrentEntity ||
        ![
          QUERY_STATUS.SUCCESS,
          QUERY_STATUS.ERROR,
          QUERY_STATUS.NOT_FOUND,
        ].includes(displaySubcontractorCompanyStatus)) && (
        <LoaderV2 loaderSize="fullpage" />
      )}
      {isCurrentEntity &&
        displaySubcontractorCompanyStatus === QUERY_STATUS.SUCCESS && (
          <>
            <InfoHeaderAction />
            {section === SECTIONS.PERSONAL && (
              <PersonalSubcontractingCompany
                readOnly={readOnly}
                setReadOnly={setReadOnly}
                onSubmit={onSubmit}
                partnerConnection={isEnabled}
                getStatus={setStatus}
                getCode={setCode}
                status={status}
                code={code}
                id={subcontractorCompany?.id}
              />
            )}
            {section === SECTIONS.SUBCONTRACTORS && (
              <SubcontractorsSubcontractingCompany
                setSlugSearched={setSlugSearched}
                subcontractors={_.map(
                  subcontractorsBySubcontractorCompany,
                  "subcontractor"
                )}
                list={historySubCompany}
                lang={lang}
                usersByIds={usersByIds}
                userScope={userScope}
                namesByFilters={namesByFilters}
              />
            )}
          </>
        )}
    </InfoContext.Provider>
  );
};

export default DisplaySubcontractingCompany;
